import { type RJSFValidationError } from '@rjsf/utils'
import { isRecord, type SchemaNode } from './schemaValues'

const _ERROR_ANNOTATION = 'x-zocks-error'

export interface SchemaErrorMessage {
  text: string
  from?: string
}

const unescapePointer = (token: string): string => token.replace(/~1/g, '/').replace(/~0/g, '~')

interface TrailNode {
  at: string
  node: SchemaNode
}

interface PointerWalk {
  current: unknown
  at: string
  trail: TrailNode[]
}

const stepInto = ({ current, at, trail }: PointerWalk, token: string): PointerWalk => {
  const key = unescapePointer(token)
  const held: unknown = Array.isArray(current) ? current[Number(key)] : isRecord(current) ? current[key] : undefined
  const stepped = `${at}/${key}`
  return { current: held, at: stepped, trail: isRecord(held) ? [...trail, { at: stepped, node: held }] : trail }
}

const nodesAlong = (schema: SchemaNode, schemaPath: string): TrailNode[] =>
  schemaPath
    .replace(/^#\/?/, '')
    .split('/')
    .filter(Boolean)
    .reduce(stepInto, { current: schema, at: '#', trail: [{ at: '#', node: schema }] }).trail

const annotationOn = (node: SchemaNode, keyword: string): string | undefined => {
  const annotated: unknown = node[_ERROR_ANNOTATION]
  if (typeof annotated === 'string') {
    return annotated.trim() || undefined
  }
  if (!isRecord(annotated)) {
    return undefined
  }
  const forKeyword: unknown = annotated[keyword]
  return typeof forKeyword === 'string' && forKeyword.trim().length > 0 ? forKeyword.trim() : undefined
}

const annotatedMessage = (schema: SchemaNode, schemaPath: string, keyword: string): SchemaErrorMessage | null =>
  [...nodesAlong(schema, schemaPath)].reverse().flatMap(({ at, node }) => {
    const text = annotationOn(node, keyword)
    return text ? [{ text, from: `${at}|${text}` }] : []
  })[0] ?? null

const paramsOf = (error: RJSFValidationError): SchemaNode => (isRecord(error.params) ? error.params : {})

const limitOf = (error: RJSFValidationError): number => Number(paramsOf(error).limit ?? 0)

const counted = (amount: number, one: string, many: string): string => `${amount} ${amount === 1 ? one : many}`

const FORMAT_TEXT: Record<string, string> = {
  date: 'should be a date',
  'date-time': 'should be a date and time',
  time: 'should be a time',
  email: 'should be an email address',
  uri: 'should be a web address',
}

const TYPE_TEXT: Record<string, string> = {
  string: 'should be text',
  number: 'should be a number',
  integer: 'should be a whole number',
  boolean: 'should be yes or no',
  array: 'should be a list',
  object: 'should be a group of fields',
}

const NOT_IN_FORMAT = 'is not in the expected format'

const NOT_AN_OPTION = 'is not one of the available options'

const DEFAULT_TEXT: Record<string, (error: RJSFValidationError) => string> = {
  pattern: () => NOT_IN_FORMAT,
  format: (error) => FORMAT_TEXT[String(paramsOf(error).format ?? '')] ?? NOT_IN_FORMAT,
  type: (error) => TYPE_TEXT[String(paramsOf(error).type ?? '')] ?? NOT_IN_FORMAT,
  maxLength: (error) => `should be ${counted(limitOf(error), 'character', 'characters')} or fewer`,
  minLength: (error) => (limitOf(error) <= 1 ? 'should not be blank' : `should be at least ${counted(limitOf(error), 'character', 'characters')}`),
  minItems: (error) => `needs at least ${counted(limitOf(error), 'entry', 'entries')}`,
  maxItems: (error) => `takes at most ${counted(limitOf(error), 'entry', 'entries')}`,
  contains: () => 'is missing an entry the custodian requires',
  uniqueItems: () => 'lists the same entry twice',
  minimum: (error) => `should be ${limitOf(error)} or more`,
  maximum: (error) => `should be ${limitOf(error)} or less`,
  exclusiveMinimum: (error) => `should be more than ${limitOf(error)}`,
  exclusiveMaximum: (error) => `should be less than ${limitOf(error)}`,
  multipleOf: (error) => `should be a multiple of ${paramsOf(error).multipleOf ?? ''}`.trim(),
  enum: () => NOT_AN_OPTION,
  const: () => NOT_AN_OPTION,
  oneOf: () => NOT_AN_OPTION,
  anyOf: () => NOT_AN_OPTION,
  not: () => 'is not a combination the custodian accepts',
}

const INSIDE_A_CHOICE = /\/(oneOf|anyOf)\/\d+\//

export const isBranchNoise = (error: RJSFValidationError): boolean => error.name === 'if' || INSIDE_A_CHOICE.test(error.schemaPath ?? '')

export const schemaErrorMessage = (schema: SchemaNode, error: RJSFValidationError): SchemaErrorMessage => {
  const keyword = error.name ?? ''
  const annotated = annotatedMessage(schema, error.schemaPath ?? '', keyword)
  if (annotated) {
    return annotated
  }
  return { text: DEFAULT_TEXT[keyword]?.(error) ?? error.message ?? '' }
}
